import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import { Link, useSearchParams } from "react-router-dom";
import {Card, StyledBody} from "baseui/card";
import {Skeleton} from "baseui/skeleton";
import {FaCheckCircle, FaUndo} from 'react-icons/fa'; // Font Awesome Icons
import Stats from "../Items/Stats";
import AuthContext from "../shared/AuthContext";
import {Spinner} from "baseui/spinner";
import QuestionnaireFilters from "../QuestionnaireFilters";
import {PLACEMENT, SnackbarProvider, useSnackbar} from 'baseui/snackbar';
import './styles/QuestionAnswer.css';
import '../../theme/qnaread-reading.css';
import {Block} from "baseui/block";
import {useStyletron} from "baseui";
import AddQues from "../Items/AddQues";
import {Modal, ModalBody, ModalButton, ModalFooter, ModalHeader} from "baseui/modal";
import {TbDatabaseExport, TbFlagCancel} from "react-icons/tb";
import AddQuestionItem from "./AddQuestionItem";
import QuestionCardItem from "../common/QuestionCardItem";
import {IoMdAddCircleOutline} from "react-icons/io";
import { orderLibraryQuestions, shouldSortLibraryByLevel, sortQuestionsByLevel } from "../../utils/questionLevelOrder";
import { invalidateRealtime } from "../../realtime/realtimeBus";
import {
    buildReadCountMap,
    LIBRARY_SORT_BY_READS,
    LIBRARY_SORT_DEFAULT,
    sortQuestionsByReadCount,
} from "../../utils/readHistoryCounts";


const QuestionnairePage = ({setIsLoading}) => {

    //States
    const {apiClient} = useContext(AuthContext); //Logout
    const [searchParams] = useSearchParams();
    const catalogPreview = searchParams.get('preview') === '1';
    const previewNotebookParam = searchParams.get('notebook') || '';
    const [filterData, setFilterData] = useState([]);
    const [questionData, setQuestionData] = useState([]);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedNoteBook, setSelectedNoteBook] = useState([]);
    const [topics, setTopics] = useState([]);
    const [selectedTopic, setSelectedTopic] = useState([]);
    const [selectedLevels, setSelectedLevels] = useState([]);
    const [selectedArchivalDate, setSelectedArchivalDate] = useState([]);
    const [selectedSubtopic, setSelectedSubTopic] = useState(filterData.subCategory2);
    const [readStatus, setReadStatus] = useState({}); // Track read status
    const [readCounts, setReadCounts] = useState({});
    const [librarySort, setLibrarySort] = useState(LIBRARY_SORT_DEFAULT);
    const [likeStatus, setLikeStatus] = useState({}); // Track read status
    const [editingIndex, setEditingIndex] = useState(null); // Track the index of the question being edited
    const [isAdmin, setIsAdmin] = useState(false);
    const [statusMessage, setStatusMessage] = useState("");
    const [placement] = useState(PLACEMENT.bottom);
    const [originalQuestionData, setOriginalQuestionData] = useState([]);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 765);
    const [isMiniMobile, setIsMiniMobile] = useState(window.innerWidth <= 375);
    const [searchLoading, setSearchLoading] = useState(false);
    const [selectorLoading, setSelectorLoading] = useState(false);
    const [reload, setReload] = useState(false);
    const [addQuesMode, setAddQuesMode] = useState(false);
    const [questionPage, setQuestionPage] = useState(0);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [totalQuestions, setTotalQuestions] = useState(0);
    const [savedIds, setSavedIds] = useState({});
    const [isFetchingMore, setIsFetchingMore] = useState(false);
    const [userProfile, setUserProfile] = useState(() => {
        const userProfile = localStorage.getItem("userProfile");
        return userProfile ? JSON.parse(userProfile) : null; // Adding a check for null
    });

    const handleSearchInputChange = (value) => {
        setSearchQuery(value);
    };

    const handleSearchOptionPick = (value) => {
        if (value != null && value !== '') {
            setSearchQuery(String(value));
        }
    };

    const handleSearchClear = () => {
        setSearchQuery('');
        setReload(true);
    };

    const searchRequestRef = useRef(0);
    const archivalValue = selectedArchivalDate?.[0]?.value || '';

    const fetchQuestionPage = useCallback(async (page, append) => {
        const notebookFilters = selectedNoteBook.map((item) => item.value);
        const useCatalogPreview = catalogPreview
            && notebookFilters.length === 1
            && previewNotebookParam
            && notebookFilters[0].toLowerCase() === previewNotebookParam.toLowerCase();
        const response = await apiClient.get('/api/questions/search', {
            params: {
                page,
                size: 40,
                q: searchQuery.trim() || undefined,
                notebook: notebookFilters,
                topic: selectedTopic.map((item) => item.value),
                level: selectedLevels.map((item) => item.value),
                archivalDate: archivalValue || undefined,
                preview: useCatalogPreview || undefined,
            },
        });
        const payload = response.data || {};
        const rawItems = Array.isArray(payload.items) ? payload.items : [];
        const items = orderLibraryQuestions(rawItems, selectedNoteBook, selectedTopic);
        const mergeItems = (prev) => {
            if (!append) return items;
            const combined = [...prev, ...items];
            if (shouldSortLibraryByLevel(selectedNoteBook, selectedTopic)) {
                return sortQuestionsByLevel(combined);
            }
            return combined;
        };
        setQuestionData(mergeItems);
        setOriginalQuestionData(mergeItems);
        setQuestionPage(payload.page ?? page);
        setHasNextPage(Boolean(payload.hasNext));
        setTotalQuestions(Number(payload.total ?? items.length));
        const total = Number(payload.total ?? items.length);
        const label = archivalValue ? `${total} on ${archivalValue}` : (searchQuery.trim() ? `${total} results` : `${total} questions`);
        setStatusMessage(label);
    }, [apiClient, searchQuery, selectedNoteBook, selectedTopic, selectedLevels, archivalValue, catalogPreview, previewNotebookParam]);

    useEffect(() => {
        const requestId = ++searchRequestRef.current;
        const timer = setTimeout(async () => {
            setSearchLoading(true);
            setIsLoading(true);
            try {
                await fetchQuestionPage(0, false);
            } catch (err) {
                if (requestId === searchRequestRef.current) {
                    setError(err.message);
                }
            } finally {
                if (requestId === searchRequestRef.current) {
                    setSearchLoading(false);
                    setIsLoading(false);
                }
            }
        }, 300);
        return () => clearTimeout(timer);
    }, [fetchQuestionPage, reload]);

    //Screen-size resizing.
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 765);
            setIsMiniMobile(window.innerWidth <= 375);
        }
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [isMobile, isMiniMobile]);

    //Check user-role.
    useEffect(() => {
        const checkUserRole = () => {
            try {
                const userProfile = localStorage.getItem("userProfile");
                if (!userProfile) return;
                
                const parsedProfile = JSON.parse(userProfile);
                if (!parsedProfile || !parsedProfile.roles) return;
                
                const status = parsedProfile.roles.includes("ROLE_ADMIN");
                if (status) {
                    setIsAdmin(status);
                }
            } catch (error) {
                console.error("Error checking user role:", error);
                setIsAdmin(false);
            }
        };
        checkUserRole();
    }, []);

    //Fetch questions data from API.
    useEffect(() => {
        setIsLoading(true);
        setSelectorLoading(true);
        const fetchQuestions = async () => {
            try {
                const readHistoryRes = await apiClient.get("/api/get-readHistory-data");

                if (readHistoryRes.status === 200 && readHistoryRes.data) {
                    let body = readHistoryRes.data;
                    if (typeof body === 'string') {
                        try {
                            body = JSON.parse(body);
                        } catch {
                            body = null;
                        }
                    }
                    const resData = body?.historyItemDto;

                    if (resData) {
                        const initialReadStatus = resData.reduce((acc, data) => {
                            acc[data.questionId] = data.readStatus;
                            return acc;
                        }, {});
                        const initialLikeStatus = resData.reduce((acc, data) => {
                            acc[data.questionId] = data.likeStatus;
                            return acc;
                        }, {});
                        setReadStatus(initialReadStatus);
                        setReadCounts(buildReadCountMap(resData));
                        setLikeStatus(initialLikeStatus);
                    }
                }
            } catch (err) {
                setError(err.message);
            }
        };

        const fetchFilterData = async () => {
            try {
                const response = await apiClient.get(`/api/get-filters`);

                if (response.status === 200) {
                    const data = response.data;
                    setFilterData(data);
                } else {
                    throw new Error(`Failed to fetch filters: ${response.status}`);
                }
            } catch (err) {
                setError(err.message);
            }
        };

        // Call API functions
        Promise.all([fetchFilterData(), fetchQuestions(), apiClient.get('/api/learning/saved/ids')])
            .then((results) => {
                const saved = results[2]?.data;
                if (Array.isArray(saved)) {
                    setSavedIds(Object.fromEntries(saved.map((id) => [id, true])));
                }
            })
            .catch((err) => setError("Failed to fetch initial data"))
            .finally(() => {
                setReload(false);
                setIsLoading(false);
                setSelectorLoading(false);
            });  // Stop loading after both calls complete
    }, [reload]);

    useEffect(() => {
        const notebookParam = searchParams.get('notebook');
        if (!notebookParam) {
            return;
        }
        setSelectedNoteBook([{ id: notebookParam, value: notebookParam }]);
        setReload(true);
    }, [searchParams]);

    const transformOptions = (options) => {
        if (!Array.isArray(options) || options.length === 0) {
            return []; // Return an empty array if options is not an array or empty
        }
        return options.map(item => ({
            id: item,
            value: item, // Ensure that 'value' is provided
        }));
    };


    const filteredQuestions = useMemo(() => {
        if (librarySort === LIBRARY_SORT_BY_READS) {
            return sortQuestionsByReadCount(questionData, readCounts);
        }
        return questionData;
    }, [questionData, readCounts, librarySort]);

    //Fetch filtered topic data.
    useEffect(() => {
        const getSelectedTopicAndSubtopic = async () => {
            try {
        if (!selectedTopic?.[0]?.value) return;
                const selectedFilterRes = await apiClient.get(`/api/get-selected-data?topic=${encodeURIComponent(selectedTopic[0].value)}`);

                if (selectedFilterRes.status < 200 || selectedFilterRes.status >= 300) {
                    throw new Error(`HTTP error! Status: ${selectedFilterRes.status}`);
                }

                const data = await selectedFilterRes.data;
                setSelectedSubTopic(data);
            } catch (err) {
                console.error("Error:", err.message);
            }
        };
        getSelectedTopicAndSubtopic();
    }, [apiClient, selectedTopic]);

    // Reset filters
    const resetFilters = () => {
        setSelectedNoteBook([]);
        setSelectedTopic([]);
        setSelectedLevels([]);
        setSelectedArchivalDate([]);
        setSearchQuery("");
        setTopics(null);
        setQuestionData(originalQuestionData);
        setReload(true);
    };

    //Update read-status for quesiton.
    const toggleReadStatus = async (questionId) => {
        const currentReadStatus = readStatus[questionId]; // Current state
        const currentLikeStatus = likeStatus[questionId];
        const updatedStatus = !currentReadStatus; // New state to toggle
        const priorCount = readCounts[questionId] ?? 0;

        // Optimistically update the state
        setReadStatus((prev) => ({...prev, [questionId]: updatedStatus}));
        if (updatedStatus) {
            setReadCounts((prev) => ({
                ...prev,
                [questionId]: priorCount + 1,
            }));
        }

        try {
            const payload = {
                readStatus: updatedStatus,
                likeStatus: Boolean(currentLikeStatus),
                isReadOnly: true
            };

            const response = await apiClient.patch(`/api/update-read-status/${questionId}`, payload);

            if (response.status !== 200) {
                throw new Error(`Failed to update read status. HTTP Status: ${response.status}`);
            }
            if (updatedStatus) {
                const { recordTeamReadProgress } = await import('../../utils/teamReadProgress');
                await recordTeamReadProgress(apiClient, questionId);
            }
            invalidateRealtime('read-toggle');
        } catch (err) {
            console.error("Error updating read status:", err.message);

            // Revert the state if the API call fails
            setReadStatus((prev) => ({...prev, [questionId]: currentReadStatus}));
            setReadCounts((prev) => ({...prev, [questionId]: priorCount}));
        }
    };

    // Handle setting the read status from child component
    const handleSetReadStatus = (questionId, newStatus) => {
        setReadStatus((prevStatus) => {
            const wasRead = prevStatus[questionId];
            if (newStatus && !wasRead) {
                setReadCounts((prev) => ({
                    ...prev,
                    [questionId]: (prev[questionId] ?? 0) + 1,
                }));
            }
            return {...prevStatus, [questionId]: newStatus};
        });
    };

    const handleTextareaResize = (e) => {
        const textarea = e.target;
        textarea.style.height = "auto"; // Reset the height to auto so it shrinks when text is deleted
        textarea.style.height = `${textarea.scrollHeight}px`; // Set the height based on scrollHeight (content height)
    };

    useEffect(() => {

    }, [selectedNoteBook])


    // Fetch topics when `selectedNoteBook` changes
    useEffect(() => {
        if (!selectedNoteBook) return;

        console.log(selectedNoteBook)
        const fetchTopics = async () => {
            setSelectorLoading(true);
            try {
                const key = encodeURIComponent(selectedNoteBook?.[0].value);
                const res = await apiClient.get(`/api/get-note-topic?key=${key}`);
                if (res.status === 200) {
                    setTopics(transformOptions(res.data)); // Transform options here
                }
            } catch (err) {
                console.error(err);
            } finally {
                setSelectorLoading(false);
            }
        };

        fetchTopics();
    }, [selectedNoteBook]);


    const handleArchivalDate = (value) => {
        if (value.length === 0) {
            setSelectedArchivalDate([]);
            setQuestionData(originalQuestionData);
        } else {
            setSelectedArchivalDate(value);
        }

    };


    // Handle delete question
    const handleDeleteClick = async (index) => {
        const questionId = filteredQuestions[index].id;

        try {
            // Make the API delete request
            const response = await apiClient.delete(`/api/delete-question?questionId=${questionId}`);

            // Check if the response status is success (typically 200 or 204)
            if (response.status === 200 || response.status === 204) {
                // Remove the question from the local state if the delete is successful
                setQuestionData((prevState) => prevState.filter((item) => item.id !== questionId));
            } else {
                throw new Error(`Failed to delete question. HTTP Status: ${response.status}`);
            }
        } catch (err) {
            console.error("Error deleting question:", err.message);
        } finally {
            setStatusMessage("Question Deleted");
        }
    };


    useEffect(() => {
        if (error) {
        }
    }, [error]);

    const toggleAddQuestion = () => {
        setAddQuesMode(!addQuesMode);
    };

    // Styles
    const styles = {
        card: {
            width: '100%',
            maxWidth: '1200px',
            margin: '0 auto',
            boxSizing: 'border-box',
        },
    };

    const [css, theme] = useStyletron();

    const filtersBlockClass = css({
        position: 'relative',
        width: '100%',
        paddingTop: theme.sizing.scale300,
        paddingLeft: theme.sizing.scale400,
        paddingRight: theme.sizing.scale400,
        paddingBottom: theme.sizing.scale600,
        boxSizing: 'border-box',
        '@media screen and (min-width: 600px)': {
            paddingLeft: theme.sizing.scale800,
            paddingRight: theme.sizing.scale800,
        },
        '@media screen and (min-width: 1024px)': {
            width: '100%',
            maxWidth: '1200px',
            marginLeft: 'auto',
            marginRight: 'auto',
            paddingLeft: theme.sizing.scale600,
            paddingRight: theme.sizing.scale600,
        },
    });

    const questionFormRef = useRef();
    const addQuestionSubmitHandler = async () => {
        const success = await questionFormRef.current.submitForm();
        if (success) {
            setStatusMessage("Question Added Successfully");
            setAddQuesMode(false);
            setReload(true);
        }
    };


    useEffect(() => {
        const handleScroll = () => {
            const scrollTop = window.scrollY;
            const windowHeight = window.innerHeight;
            const fullHeight = document.body.offsetHeight;

            if (
                scrollTop + windowHeight >= fullHeight - 160 &&
                !isFetchingMore &&
                hasNextPage
            ) {
                setIsFetchingMore(true);
                fetchQuestionPage(questionPage + 1, true)
                    .catch((err) => setError(err.message))
                    .finally(() => setIsFetchingMore(false));
            }
        };

        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, [questionPage, hasNextPage, isFetchingMore, fetchQuestionPage, archivalValue]);

    const handleAddQuestion = () => {
        setReload(true);
    };

    const toggleSaved = async (questionId) => {
        const currentlySaved = Boolean(savedIds[questionId]);
        setSavedIds((prev) => ({ ...prev, [questionId]: !currentlySaved }));
        try {
            if (currentlySaved) {
                await apiClient.delete(`/api/learning/saved/${questionId}`);
            } else {
                await apiClient.put(`/api/learning/saved/${questionId}`);
            }
            invalidateRealtime('save-toggle');
        } catch (err) {
            setSavedIds((prev) => ({ ...prev, [questionId]: currentlySaved }));
            setError(err.message);
        }
    };

    return (
        <div className="questionnaire-page">
            <SnackbarProvider
                placement={placement}
                overrides={{
                    Body: {
                        style: {
                            fontSize: '12px',
                            paddingLeft: '12px',
                            paddingRight: '12px',
                            paddingTop: '10px',
                            paddingBottom: '10px',
                            maxWidth: 'min(360px, 92vw)',
                        },
                    },
                }}>
                {statusMessage && <SnackbarMessage statusMessage={statusMessage}/>}
                {catalogPreview && previewNotebookParam && (
                    <div className="library-preview-banner" role="status">
                        <p>
                            Previewing <strong>{previewNotebookParam}</strong> — subscribe on{' '}
                            <Link to="/hub#learn-library">Learn</Link> to keep this notebook in your Library.
                        </p>
                    </div>
                )}
                <Block className={`questionnaire-filters questionnaire-filters-panel ${filtersBlockClass}`}>
                    <QuestionnaireFilters
                        searchQuery={searchQuery}
                        onSearchInputChange={handleSearchInputChange}
                        onSearchOptionPick={handleSearchOptionPick}
                        onSearchClear={handleSearchClear}
                        searchLoading={searchLoading}
                        autoFocusSearch={!isMobile}
                        filterData={filterData}
                        topics={topics}
                        selectedNoteBook={selectedNoteBook}
                        selectedTopic={selectedTopic}
                        selectedLevels={selectedLevels}
                        selectedArchivalDate={selectedArchivalDate}
                        selectorLoading={selectorLoading}
                        onNotebookChange={setSelectedNoteBook}
                        onTopicChange={setSelectedTopic}
                        onLevelChange={setSelectedLevels}
                        onArchivalChange={handleArchivalDate}
                        transformOptions={transformOptions}
                        originalQuestionData={originalQuestionData}
                        librarySort={librarySort}
                        onLibrarySortChange={setLibrarySort}
                    />
                </Block>

                <Card
                    className="questionnaire-card-list"
                    overrides={{Root: {style: styles.card}}}
                >
                    {/* Top-right tag */}
                    <StyledBody>
                        {filteredQuestions.length > 0 ? (
                            filteredQuestions.map((questionItem, index) => (
                                <QuestionCardItem
                                    key={questionItem.id ?? index}
                                    index={index}
                                    questionItem={questionItem}
                                    toggleReadStatus={toggleReadStatus}
                                    readStatus={readStatus}
                                    readCount={readCounts[questionItem.id] ?? 0}
                                    liked={Boolean(likeStatus[questionItem.id])}
                                    handleSetReadStatus={handleSetReadStatus}
                                    handleDeleteClick={handleDeleteClick}
                                    setStatusMessage={setStatusMessage}
                                    editingIndex={editingIndex}
                                    isAdmin={isAdmin}
                                    handleTextareaResize={handleTextareaResize}
                                    setQuestionData={setQuestionData}
                                    saved={Boolean(savedIds[questionItem.id])}
                                    onToggleSave={toggleSaved}
                                    userId={userProfile?.id}
                                />
                            ))
                        ) : searchLoading ? (
                            <div>
                                <Skeleton rows={10} height="200px" width="100%" animation autoSizeRows />
                                <br />
                                <Skeleton rows={10} height="200px" width="100%" animation autoSizeRows />
                            </div>
                        ) : (
                            <div className="library-scope-empty" role="status">
                                <p className="library-scope-empty-title">No questions to show</p>
                                <p className="library-scope-empty-lead">
                                    Library is limited to onboarding modules, notebooks you subscribe to on Learn, and
                                    your own Learn imports. Subscribe or pick modules to see questions here.
                                </p>
                                <Link to="/hub#learn-library" className="library-scope-empty-link">
                                    Open Learn — subscribe or add notebooks
                                </Link>
                            </div>
                        )}

                        {isFetchingMore && (
                            <div style={{textAlign: "-webkit-center"}}>
                                <Spinner $size={20} $color="black"/>
                                <br/>
                                <Skeleton
                                    rows={10}
                                    height="300px"
                                    width="100%"
                                    animation
                                    autoSizeRows
                                />
                                <br/>
                                <Skeleton
                                    rows={10}
                                    height="300px"
                                    width="100%"
                                    animation
                                    autoSizeRows
                                />
                                <br/>
                                <Skeleton
                                    rows={10}
                                    height="300px"
                                    width="100%"
                                    animation
                                    autoSizeRows
                                />
                            </div>
                        )}
                    </StyledBody>
                </Card>
            </SnackbarProvider>
            <div>
                <Stats
                    className="stats-btn"
                    topic={selectedTopic}
                    data={filteredQuestions}
                    readHistory={readStatus}
                />
                <AddQues
                    className="addQues-btn"
                    toggleAddQuestion={toggleAddQuestion}
                />
            </div>
            <div className="add-ques-modal">
                <Modal
                    onClose={() => setAddQuesMode(false)}
                    isOpen={addQuesMode}
                    overrides={{
                        Dialog: {
                            style: {
                                width: 'min(80vw, 720px)',
                                maxWidth: '96vw',
                                height: 'min(80vh, 720px)',
                                maxHeight: '92vh',
                                display: 'flex',
                                flexDirection: 'column',
                            },
                        },
                    }}
                >
                    <ModalHeader>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                            <IoMdAddCircleOutline size={24} />
                            <span>Add New Question</span>
                        </div>
                    </ModalHeader>
                    <ModalBody
                        style={{
                            flex: "1 1 auto",
                            maxHeight: "70vh",  // or any appropriate value
                            overflow: "auto"
                        }}
                    >
                        <AddQuestionItem ref={questionFormRef} onAdd={handleAddQuestion} />
                    </ModalBody>
                    <ModalFooter>
                        <ModalButton kind="secondary" onClick={() => setAddQuesMode(false)}>
                            <TbFlagCancel/>&nbsp;Cancel
                        </ModalButton>
                        <ModalButton onClick={addQuestionSubmitHandler}>
                            <TbDatabaseExport/>&nbsp;
                            Submit</ModalButton>
                    </ModalFooter>
                </Modal>
            </div>
        </div>
    );
};

// SnackbarMessage Component to handle the Snackbar UI
const SnackbarMessage = ({statusMessage, type = "success"}) => {
    const {enqueue} = useSnackbar();
    useEffect(() => {
        const enhancer = type === "success" ? FaCheckCircle : FaUndo;
        if (statusMessage) {
            enqueue({message: statusMessage, startEnhancer: enhancer});
        }
    }, [statusMessage, enqueue, type]);

    return null;
};

export default QuestionnairePage;
